import {
  Box,
  Card,
  CardActionArea,
  CardActions,
  CardHeader,
  Grid,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import { AdVideo, Brand, ChangeEventType } from '../../../types';
import {
  AdsTemplate,
  CreateCampaignForm,
  DiySettings,
} from '../../../types/IDiy';
import { DEFAULT_COLOR_THEME, ScrollStyle } from '../../../utils';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { AdsPreviewSquare } from './AdsPreview';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import VideoCameraBackOutlinedIcon from '@mui/icons-material/VideoCameraBackOutlined';
import Spinner from '../../Spinner';
import ModalHeader from '../../Modal/ModalHeader';
import { PrimaryButton } from '../../Buttons';
import AddIcon from '@mui/icons-material/Add';

interface DiyTemplateFormProps {
  brand: Brand;
  templates: AdsTemplate[];
  filteredTemplates: AdsTemplate[];
  setFilteredTemplates: Dispatch<SetStateAction<AdsTemplate[]>>;
  selectedTemplate: AdsTemplate;
  setSelectedTemplate: Dispatch<SetStateAction<AdsTemplate>>;
  diySettings: DiySettings;
  adVideo: AdVideo;
  onShowMobilePreview: () => void;
  adVideoLoading: boolean;
  formValues: CreateCampaignForm;
  isEdit: boolean;
  openTemplateForm: boolean;
  setOpenTemplateForm: Dispatch<SetStateAction<boolean>>;
  templateAction: 'create' | 'edit' | 'duplicate';
  setTemplateAction: Dispatch<SetStateAction<'create' | 'edit' | 'duplicate'>>;
  loading: boolean;
  onSelectTemplate: (template: AdsTemplate) => void;
}

const DiyTemplateForm: FC<DiyTemplateFormProps> = ({
  brand,
  templates,
  filteredTemplates,
  setFilteredTemplates,
  selectedTemplate,
  setSelectedTemplate,
  diySettings,
  adVideo,
  onShowMobilePreview,
  adVideoLoading,
  formValues,
  isEdit,
  setOpenTemplateForm,
  setTemplateAction,
  loading,
  onSelectTemplate,
}) => {
  const theme = useTheme();
  const xsOnly = useMediaQuery(theme.breakpoints.only('xs'));
  const [search, setSearch] = useState<string>('');
  const [filter, setFilter] = useState<string>('all');

  useEffect(() => {
    if (
      isEdit &&
      templates.length > 0 &&
      formValues?.adTemplate?.template &&
      !selectedTemplate
    ) {
      const selected = templates.find(
        (template: AdsTemplate) =>
          template._id === formValues?.adTemplate?.template,
      );

      if (selected) onSelectTemplate(selected);
    }
  }, [templates, isEdit, formValues?.adTemplate?.template, selectedTemplate]);

  const handleOnSearch = (e: ChangeEventType) => {
    setSearch(e.target.value);
    const temp = templates.filter((template: AdsTemplate) =>
      template.name.toLowerCase().includes(e.target.value.toLowerCase()),
    );

    setFilteredTemplates(temp);
  };

  const handleOnChangeFilter = (e: any, filter: string) => {
    setFilter(filter);
    setSelectedTemplate(null);

    let temp: AdsTemplate[] = [];
    if (filter === 'image') {
      temp = templates.filter(
        (template: AdsTemplate) => template.media === 'image',
      );
    } else if (filter === 'video') {
      temp = templates.filter(
        (template: AdsTemplate) => template.media === 'video',
      );
    } else {
      temp = templates;
    }

    setFilteredTemplates(temp);
  };

  const handleOnClickCreateTemplate = () => {
    setOpenTemplateForm(true);
    setTemplateAction('create');
  };

  const handleOnClickEditTemplate = (template: AdsTemplate) => {
    setOpenTemplateForm(true);
    setTemplateAction('edit');
    setSelectedTemplate(template);
  };

  const handleOnClickDuplicateTemplate = (template: AdsTemplate) => {
    setOpenTemplateForm(true);
    setTemplateAction('duplicate');
    setSelectedTemplate(template);
  };

  const scrollStyling = {
    minHeight: `calc(100vh - 250px)`,
    maxHeight: `calc(100vh - 250px)`,
    overflowY: 'auto',
    overflowX: 'hidden',
    ...ScrollStyle(),
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={4} sx={!xsOnly ? scrollStyling : {}}>
        <Grid
          container
          spacing={2}
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 999,
            backgroundColor: 'white',
            mb: 2,
          }}
        >
          <Grid item xs={12}>
            <PrimaryButton
              title="Add New Template"
              type="button"
              handleOnClick={handleOnClickCreateTemplate}
              startIcon={<AddIcon />}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              type="text"
              id="search"
              name="search"
              label="Search"
              onChange={handleOnSearch}
              value={search}
              InputLabelProps={{ shrink: true }}
              size="small"
              placeholder="Search by template name"
            />
          </Grid>

          <Grid
            item
            xs={12}
            sx={{
              justifyContent: 'space-between',
              display: 'flex',
            }}
          >
            <ToggleButtonGroup
              sx={{ color: DEFAULT_COLOR_THEME }}
              value={filter}
              exclusive
              onChange={handleOnChangeFilter}
            >
              {['all', 'image', 'video']?.map((filter: string) => {
                const icon =
                  filter === 'all' ? (
                    'All'
                  ) : filter === 'image' ? (
                    <ImageOutlinedIcon />
                  ) : (
                    <VideoCameraBackOutlinedIcon />
                  );

                return (
                  <ToggleButton key={filter} value={filter}>
                    {icon}
                  </ToggleButton>
                );
              })}
            </ToggleButtonGroup>

            {xsOnly && selectedTemplate ? (
              <PrimaryButton
                title="Preview"
                type="button"
                variant="text"
                handleOnClick={onShowMobilePreview}
              />
            ) : null}
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ px: 1, paddingY: '10px' }}>
          {!loading ? (
            filteredTemplates.map((template: AdsTemplate) => {
              const selected = selectedTemplate?._id === template._id;

              const icon =
                template?.media === 'image' ? (
                  <ImageOutlinedIcon sx={{ fontSize: '30px' }} />
                ) : (
                  <VideoCameraBackOutlinedIcon sx={{ fontSize: '30px' }} />
                );

              return (
                <Grid
                  item
                  xs={12}
                  key={template._id}
                  sx={{
                    position: 'relative',
                  }}
                >
                  {selected ? (
                    <Box
                      component="div"
                      sx={{
                        position: 'absolute',
                        top: '8px',
                        zIndex: 1,
                        right: '-10px',
                        float: 'right',
                        margin: '1px',
                      }}
                    >
                      <CheckCircleIcon
                        sx={{
                          color: DEFAULT_COLOR_THEME,
                          bgcolor: 'InfoBackground',
                          borderRadius: '50%',
                        }}
                      />
                    </Box>
                  ) : null}

                  <Card
                    sx={{
                      height: '100%',
                      border: selected ? 'solid' : '',
                      borderColor: selected ? DEFAULT_COLOR_THEME : '',
                      borderWidth: 'medium',
                      zIndex: 0,
                    }}
                  >
                    <CardActionArea onClick={() => onSelectTemplate(template)}>
                      <CardHeader
                        avatar={icon}
                        title={template.name}
                        subheader={template.description}
                        titleTypographyProps={{
                          variant: 'body1',
                          sx: { fontWeight: 'bold' },
                        }}
                        subheaderTypographyProps={{ variant: 'body2' }}
                      />
                    </CardActionArea>

                    <CardActions
                      sx={{ justifyContent: 'flex-end', display: 'flex' }}
                    >
                      <PrimaryButton
                        title="Edit"
                        type="button"
                        variant="text"
                        handleOnClick={() =>
                          handleOnClickEditTemplate(template)
                        }
                      />

                      <PrimaryButton
                        title="Duplicate"
                        type="button"
                        variant="text"
                        handleOnClick={() =>
                          handleOnClickDuplicateTemplate(template)
                        }
                      />
                    </CardActions>
                  </Card>
                </Grid>
              );
            })
          ) : (
            <Grid
              item
              xs={12}
              sx={{ justifyContent: 'center', display: 'flex' }}
              mt={2}
            >
              <Spinner />
            </Grid>
          )}
        </Grid>
      </Grid>

      {/** Ad Preview Section */}
      {!xsOnly && selectedTemplate ? (
        <Grid item xs={12} sm={8} sx={scrollStyling}>
          <Grid container spacing={2} sx={{ position: 'sticky', top: 0 }}>
            <Grid
              item
              xs={12}
              sx={{ justifyContent: 'center', display: 'flex' }}
            >
              <ModalHeader title="Ad Preview" />
            </Grid>

            <Grid
              item
              xs={12}
              sx={{
                justifyContent: 'center',
                display: 'flex',
                paddingY: '10px',
              }}
            >
              {!adVideoLoading ? (
                <AdsPreviewSquare
                  page={diySettings?.facebookPage}
                  body={
                    selectedTemplate?.creativeTemplate?.asset_feed_spec
                      ?.bodies[0]?.text
                  }
                  isImage={selectedTemplate?.media === 'image'}
                  imageUrl={selectedTemplate?.images?.square}
                  websiteUrl={
                    selectedTemplate?.creativeTemplate?.asset_feed_spec
                      ?.link_urls[0]?.website_url
                  }
                  headline={
                    selectedTemplate?.creativeTemplate?.asset_feed_spec
                      ?.titles[0]?.text
                  }
                  description={
                    selectedTemplate?.creativeTemplate?.asset_feed_spec
                      ?.descriptions[0]?.text
                  }
                  cta={
                    selectedTemplate?.creativeTemplate?.asset_feed_spec
                      ?.call_to_action_types[0]
                  }
                  videoUrl={adVideo?.source}
                  objective={formValues?.campaignTemplate?.objective}
                  brand={brand}
                />
              ) : (
                <Spinner />
              )}
            </Grid>
          </Grid>
        </Grid>
      ) : null}
    </Grid>
  );
};

export default DiyTemplateForm;
